import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const SolutionSection = styled.section`
  padding: 120px 0;
  background: 
    radial-gradient(circle at 20% 80%, #00D4FF10 0%, transparent 50%),
    linear-gradient(180deg, ${props => props.theme.colors.darkGray} 0%, ${props => props.theme.colors.dark} 100%);
  position: relative;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('/Perfil carros no fundo.jpg');
    background-size: cover;
    background-position: center;
    opacity: 0.05;
    z-index: 0;
  }

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: repeating-linear-gradient(
      -45deg,
      transparent,
      transparent 150px,
      ${props => props.theme.colors.primary}03 152px
    );
    z-index: 1;
    pointer-events: none;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const Title = styled(motion.h2)`
  font-family: ${props => props.theme.fonts.display};
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 700;
  margin-bottom: 24px;
  background: linear-gradient(135deg, ${props => props.theme.colors.white} 0%, ${props => props.theme.colors.primary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled(motion.p)`
  font-size: 1.25rem;
  color: ${props => props.theme.colors.lightGray};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

const SolutionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 40px;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const SolutionCard = styled(motion.div)`
  background: linear-gradient(135deg, ${props => props.theme.colors.mediumGray}40, ${props => props.theme.colors.darkGray}60);
  border: 1px solid ${props => props.theme.colors.primary}30;
  border-radius: 16px;
  padding: 40px;
  text-align: center;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 24px;
    border-radius: 12px;
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
    transition: height 0.3s ease;
  }

  @media (hover: hover) {
    &:hover {
      transform: translateY(-8px);
      border-color: ${props => props.theme.colors.primary}60;
      box-shadow: 0 20px 40px rgba(0, 212, 255, 0.2);

      &:before {
        height: 8px;
      }
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    &:active {
      transform: scale(0.98);
      border-color: ${props => props.theme.colors.primary}60;
    }
  }
`;

const SolutionIcon = styled(motion.div)`
  width: 80px;
  height: 80px;
  border-radius: 16px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}20, ${props => props.theme.colors.secondary}20);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 32px;
  transition: all 0.3s ease;
`;

const SolutionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.white};
  margin-bottom: 16px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.25rem;
  }
`;

const SolutionDescription = styled.p`
  color: ${props => props.theme.colors.lightGray};
  line-height: 1.6;
  margin-bottom: 20px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 0.95rem;
  }
`;

const SolutionResult = styled.div`
  background: ${props => props.theme.colors.success}10;
  border: 1px solid ${props => props.theme.colors.success}30;
  border-radius: 8px;
  padding: 16px;
  color: ${props => props.theme.colors.success};
  font-weight: 500;
  font-size: 0.9rem;
`;

const Solution = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const solutions = [
    {
      icon: "1",
      title: "Fundamentos da LM Motors",
      description: "Conheça a realidade do cenário automotivo e entenda por que a consignação precisa ter propósito.",
      result: "Base sólida para o negócio"
    },
    {
      icon: "2",
      title: "Consignação Lucrativa",
      description: "Passo a passo para consignar sem dor de cabeça, escolher carros certos e calcular margem ideal.",
      result: "Lucro sem riscos"
    },
    {
      icon: "3",
      title: "Marketing e Negociação",
      description: "Técnicas de marketing da atração, negociação eficaz e como gerar leads qualificados.",
      result: "Vendas mais assertivas"
    },
    {
      icon: "$",
      title: "Cálculo de Margem Ideal",
      description: "Aprenda a precificar corretamente cada veículo para maximizar lucros sem perder vendas.",
      result: "Precificação otimizada"
    },
    {
      icon: "⚡",
      title: "Resolução de Problemas",
      description: "Como resolver problemas na consignação sem criar novos problemas para seu negócio.",
      result: "Menos dor de cabeça"
    },
    {
      icon: "▶",
      title: "Poder da Autoridade",
      description: "Use sua autoridade no mercado para atrair melhores negócios e clientes qualificados.",
      result: "Posição de referência"
    }
  ];

  return (
    <SolutionSection ref={ref}>
      <Container>
        <Header>
          <Title
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            A SOLUÇÃO COMPLETA PARA SEU SUCESSO
          </Title>
          <Subtitle
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Descubra o método CCP que já transformou centenas de lojistas em verdadeiras máquinas de lucro
          </Subtitle>
        </Header>

        <SolutionsGrid>
          {solutions.map((solution, index) => (
            <SolutionCard
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -5, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <SolutionIcon
                whileHover={{ scale: 1.1, rotate: 3 }}
                transition={{ duration: 0.2 }}
              >
                {solution.icon}
              </SolutionIcon>
              <SolutionTitle>{solution.title}</SolutionTitle>
              <SolutionDescription>{solution.description}</SolutionDescription>
              <SolutionResult>{solution.result}</SolutionResult>
            </SolutionCard>
          ))}
        </SolutionsGrid>
      </Container>
    </SolutionSection>
  );
};

export default Solution;